/* /gov — port of prototype pgGov + govNewModal + feeCalc (gaps.js), plus renewal guides and the integrations link. */
import { useId, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAllEmployees, useCreateGovTransaction, useGovFee, useGovTransactions, useToday } from "../../data/hooks";
import { ApiError } from "../../data/provider";
import type { Employee, FeeResult } from "../../data/types";
import { absenceReportBlocked, GOV_TYPES } from "../../domain/compliance";
import { daysTo } from "../../domain/dates";
import { useI18n } from "../../i18n";
import { gDate } from "../../lib/format";
import { Alert, Button, Card, Chip, EmptyState, ErrorBox, Field, Kpi, Kv, Modal, Money, PageHeader, Person, Select, Skeleton, Tabs, Toggle, errMsg, useToast } from "../../ui";
import RenewalGuide from "./components/RenewalGuide";
import { GUIDES, guideKindFor } from "./components/renewals";
import { REL_AR } from "./components/shared";
import { useCountry } from "../../lib/country";
import GovCountry from "./components/GovCountry";
import "./core.css";

type Tab = "transactions" | "fees" | "dependants" | "guides";
export const LEVY = 800, DEP_FEE = 400, IQAMA_FEE = 650;

const TYPE_AR: Record<string, string> = {
  "Iqama renewal": "تجديد الإقامة", "Exit re-entry (single)": "خروج وعودة (مفردة)", "Exit re-entry (multiple)": "خروج وعودة (متعددة)",
  "Final exit": "خروج نهائي", "Absence report (Huroob)": "بلاغ تغيب عن العمل",
};
const STATUS_TONE: Record<string, string> = { Completed: "ok", Draft: "", "In progress": "info", "Awaiting approval": "warn", Submitted: "info" };
const STATUS_AR: Record<string, string> = { Completed: "مكتملة", Draft: "مسودة", "In progress": "قيد التنفيذ", "Awaiting approval": "بانتظار الموافقة", Submitted: "مرسلة" };
export const periodsFor = (type: string) => (type === "Iqama renewal" ? [3, 6, 9, 12] : /^Exit re-entry/.test(type) ? [2, 3, 4, 6, 12] : []);

function FeeBreakdown({ fee }: { fee: FeeResult }) {
  const { L, t } = useI18n();
  return (
    <Kv items={[
      ...(fee.iq != null ? [[L("Iqama", "الإقامة"), <Money value={fee.iq} />], [L("Expat levy", "المقابل المالي"), <Money value={fee.levy ?? 0} />], [L("Work permit", "رخصة العمل"), <Money value={fee.wp ?? 0} />]] as [string, ReactNode][] : []),
      [<b>{t("Total")}</b>, <b data-testid="fee-total"><Money value={fee.total} /></b>],
    ]} />
  );
}

function FeeCalculator() {
  const { L, t } = useI18n();
  const [type, setType] = useState<string>("Iqama renewal");
  const [months, setMonths] = useState(12);
  const ps = periodsFor(type);
  const m = ps.length ? (ps.includes(months) ? months : ps[ps.length - 1]) : 0;
  const q = useGovFee(type, m);
  const tId = useId(), mId = useId();
  return (
    <Card title={L("Fee calculator", "حاسبة الرسوم")} sub={L("Same rates the API uses", "نفس الأسعار المعتمدة في النظام")} testId="fee-calculator">
      <div className="form2">
        <Field label={L("Transaction", "المعاملة")} htmlFor={tId}><Select id={tId} value={type} onChange={(e) => setType(e.target.value)} options={GOV_TYPES.map((x) => [x, L(x, TYPE_AR[x] ?? x)] as [string, string])} data-testid="calc-type" /></Field>
        {ps.length > 0 && <Field label={L("Period", "المدة")} htmlFor={mId}><Select id={mId} value={m} onChange={(e) => setMonths(Number(e.target.value))} options={ps.map((x) => [x, x + " " + t("months")] as [number, string])} data-testid="calc-months" /></Field>}
      </div>
      <div className="mt">{q.error ? <ErrorBox error={q.error} /> : q.data ? <FeeBreakdown fee={q.data} /> : <div className="skel" style={{ height: 40 }} />}</div>
    </Card>
  );
}

function NewTxModal({ emps, today, onClose }: { emps: Employee[]; today: string; onClose: () => void }) {
  const i = useI18n();
  const { L, t, lang } = i;
  const toast = useToast();
  const create = useCreateGovTransaction();
  const expats = emps.filter((e) => e.nationality !== "SA" && e.status !== "Terminated");
  const [empId, setEmpId] = useState<number>(expats[0]?.id ?? 0);
  const [type, setType] = useState<string>("Iqama renewal");
  const [months, setMonths] = useState(12);
  const [recover, setRecover] = useState(false);
  const e = expats.find((x) => x.id === empId);
  const ps = periodsFor(type);
  const m = ps.length ? (ps.includes(months) ? months : ps[ps.length - 1]) : 0;
  const fee = useGovFee(type, m);
  const iqLeft = e?.iqamaExpiry ? daysTo(today, e.iqamaExpiry) : null;
  const blocked = type === "Absence report (Huroob)" && !!e && absenceReportBlocked(e, today);
  const gk = guideKindFor(type);
  const eId = useId(), tId = useId(), mId = useId();

  const submit = async () => {
    if (!e) return;
    try {
      const r = await create.mutateAsync({ employeeId: e.id, type, period: m });
      toast(L("Submitted to Muqeem · fees SAR " + r.fee.toLocaleString("en-US"), "أُرسلت إلى مقيم · الرسوم " + r.fee.toLocaleString("en-US") + " ر.س") + (recover ? L(" · recovery added to payroll", " · أُضيف الاسترداد إلى الرواتب") : ""));
      onClose();
    } catch (err) {
      toast(err instanceof ApiError && err.status === 409 ? L("Qiwa blocks this absence report: the Iqama needs at least 60 days left.", "تمنع قوى هذا البلاغ: يجب أن يتبقى على الإقامة 60 يومًا على الأقل.") : errMsg(err), "bad");
    }
  };

  return (
    <Modal title={L("New government transaction", "معاملة حكومية جديدة")} onClose={onClose} testId="gov-new-modal"
      foot={<><Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button><Button variant="pri" disabled={blocked || !e} loading={create.isPending} onClick={submit} data-testid="gov-submit">{L("Submit to Muqeem", "إرسال إلى مقيم")}</Button></>}>
      <div className="stack">
        {!expats.length ? <EmptyState title={L("No non-Saudi employees", "لا يوجد موظفون غير سعوديين")} /> : (
          <>
            <Field label={t("Employee")} htmlFor={eId}><Select id={eId} value={empId} onChange={(ev) => setEmpId(Number(ev.target.value))} options={expats.map((x) => [x.id, i.name(x) + (x.iqamaExpiry ? " · " + L("Iqama ", "إقامة ") + gDate(x.iqamaExpiry, lang) : "")] as [number, string])} data-testid="gov-emp" /></Field>
            <Field label={L("Transaction", "المعاملة")} htmlFor={tId}><Select id={tId} value={type} onChange={(ev) => setType(ev.target.value)} options={GOV_TYPES.map((x) => [x, L(x, TYPE_AR[x] ?? x)] as [string, string])} data-testid="gov-type" /></Field>
            {ps.length > 0 && <Field label={L("Period", "المدة")} htmlFor={mId}><Select id={mId} value={m} onChange={(ev) => setMonths(Number(ev.target.value))} options={ps.map((x) => [x, x + " " + t("months")] as [number, string])} data-testid="gov-months" /></Field>}
            {fee.data && fee.data.total > 0 && <Card title={L("Fees", "الرسوم")} className="pad" testId="gov-fees"><FeeBreakdown fee={fee.data} /></Card>}
            {blocked && <Alert tone="bad" tint testId="absence-blocked" title={L("Can't report absence yet", "لا يمكن الإبلاغ عن الغياب")} desc={L("Qiwa only allows an absence report when the Iqama has at least 60 days left and there's no active contract (rule from 31 Jul 2025). This Iqama has " + iqLeft + " days.", "تسمح قوى بالإبلاغ فقط إذا بقي على الإقامة 60 يومًا على الأقل ولا يوجد عقد ساري (منذ 31 يوليو 2025). المتبقي على هذه الإقامة " + iqLeft + " يومًا.")} />}
            {type === "Final exit" && <Alert tone="info" tint testId="final-exit-note" title={L("Before final exit", "قبل الخروج النهائي")} desc={L("Pay the final settlement, end the Qiwa contract and cancel medical insurance. We'll remind you in the offboarding checklist.", "اصرف المخالصة وأنهِ عقد قوى وألغِ التأمين الطبي. سنذكّرك بذلك في قائمة إنهاء الخدمة.")} />}
            <div className="row nw" style={{ gap: 10 }}><Toggle checked={recover} onChange={setRecover} label={L("Recover cost from employee through payroll", "استرداد التكلفة من الموظف عبر الرواتب")} testId="gov-recover" /><span className="sm">{L("Recover cost from employee through payroll", "استرداد التكلفة من الموظف عبر الرواتب")}</span></div>
            {gk && <RenewalGuide kind={gk} compact audience="employer" />}
          </>
        )}
      </div>
    </Modal>
  );
}

/** Saudi Arabia: Muqeem / Qiwa fees and services. */
function SaudiGov() {
  const i = useI18n();
  const { t, L, isAr } = i;
  const nav = useNavigate();
  const today = useToday();
  const [tab, setTab] = useState<Tab>("transactions");
  const [modal, setModal] = useState(false);
  const txQ = useGovTransactions();
  const empsQ = useAllEmployees();

  const err = txQ.error || empsQ.error;
  if (err) return <ErrorBox error={err} onRetry={() => { txQ.refetch(); empsQ.refetch(); }} />;
  if (!txQ.data || !empsQ.data) return <Skeleton />;

  const tx = txQ.data;
  const emps = empsQ.data;
  const byId = new Map(emps.map((e) => [e.id, e] as [number, Employee]));
  const ex = emps.filter((e) => e.nationality !== "SA" && e.status !== "Terminated");
  const deps = ex.reduce((a, e) => a + e.dependants.length, 0);
  const month = today.slice(0, 7);
  const paidMonth = tx.filter((g) => g.date.slice(0, 7) === month).reduce((a, g) => a + g.fee, 0);
  const open = tx.filter((g) => g.status !== "Completed").length;
  const withDeps = emps.filter((e) => e.dependants.length && e.status !== "Terminated");

  let body;
  if (tab === "transactions") body = tx.length ? (
    <div className="tw">
      <table className="tbl cards" data-testid="gov-table">
        <thead><tr><th>{t("Employee")}</th><th>{L("Transaction", "المعاملة")}</th><th className="hide-md">{L("Platform ref.", "مرجع المنصة")}</th><th className="end">{L("Fees", "الرسوم")}</th><th>{t("Status")}</th></tr></thead>
        <tbody>
          {tx.map((g) => {
            const e = byId.get(g.employeeId);
            return (
              <tr key={g.id} data-testid={"gov-row-" + g.id}>
                <td className="k1">{e ? <Person e={e} meta={i.nat(e.nationality)} /> : "#" + g.employeeId}</td>
                <td className="k3" data-l={L("Transaction", "المعاملة")}>{L(g.type, TYPE_AR[g.type] ?? g.type)}{g.period ? <> · <span className="num">{g.period}</span> {t("months")}</> : null}</td>
                <td className="hide-md num sm">{g.ref}</td>
                <td className="end x" data-l={L("Fees", "الرسوم")}>{g.fee ? <Money value={g.fee} dec={0} /> : "—"}</td>
                <td className="k2"><Chip tone={STATUS_TONE[g.status] ?? "info"}>{isAr ? STATUS_AR[g.status] ?? g.status : g.status}</Chip></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  ) : <EmptyState title={L("No transactions yet", "لا توجد معاملات بعد")} desc={L("Iqama renewals, visas and exits you submit will appear here.", "ستظهر هنا تجديدات الإقامة والتأشيرات وطلبات الخروج التي ترسلها.")} />;

  if (tab === "fees") body = (
    <div className="card-b">
      <div className="g g-4 mb" data-testid="fee-kpis">
        <Kpi testId="kpi-levy" label={L("Expat levy / month", "المقابل المالي / شهر")} value={(ex.length * LEVY).toLocaleString("en-US")} unit={L("SAR", "ر.س")} icon="banknote" foot={ex.length + " × " + LEVY} />
        <Kpi testId="kpi-deps" label={L("Dependants fee / month", "رسوم المرافقين / شهر")} value={(deps * DEP_FEE).toLocaleString("en-US")} unit={L("SAR", "ر.س")} icon="users" tone="info" foot={deps + " × " + DEP_FEE} />
        <Kpi testId="kpi-iqama" label={L("Iqama fees / year", "رسوم الإقامة / سنة")} value={(ex.length * IQAMA_FEE).toLocaleString("en-US")} unit={L("SAR", "ر.س")} icon="id-card" tone="gold" />
        <Kpi testId="kpi-paid" label={L("Paid this month", "المدفوع هذا الشهر")} value={paidMonth.toLocaleString("en-US")} unit={L("SAR", "ر.س")} icon="receipt" />
      </div>
      <div className="g g-2-1">
        <FeeCalculator />
        <Alert tone="info" tint title={L("Fee rates used", "الرسوم المعتمدة")} desc={L("Levy SAR 800/month per expat, dependants SAR 400/month each, Iqama SAR 650/year, exit re-entry single SAR 200 for 2 months + 100/extra month, multiple SAR 500 for 3 months + 200/extra month. Edit in Settings if rates change.", "المقابل المالي 800 ر.س شهريًا لكل وافد، والمرافق 400 ر.س شهريًا، والإقامة 650 ر.س سنويًا، والخروج والعودة المفردة 200 ر.س لشهرين + 100 لكل شهر إضافي، والمتعددة 500 ر.س لثلاثة أشهر + 200 لكل شهر إضافي.")} />
      </div>
    </div>
  );

  if (tab === "dependants") body = withDeps.length ? (
    <div className="tw">
      <table className="tbl cards" data-testid="deps-table">
        <thead><tr><th>{t("Employee")}</th><th>{L("Dependants", "المرافقون")}</th><th>{L("CCHI class", "فئة التأمين")}</th><th className="end">{L("Dependants fee / mo", "رسوم المرافقين")}</th></tr></thead>
        <tbody>
          {withDeps.map((e) => (
            <tr key={e.id} className="click" onClick={() => nav("/employees/" + e.id + "?tab=deps")} data-testid={"dep-row-" + e.id}>
              <td className="k1"><Person e={e} /></td>
              <td className="k3" data-l="">{e.dependants.map((d) => L(d.relation, REL_AR[d.relation] ?? d.relation)).join(L(", ", "، "))}</td>
              <td className="x" data-l="">{e.cchiClass}</td>
              <td className="end k2">{e.nationality !== "SA" ? <Money value={e.dependants.length * DEP_FEE} dec={0} /> : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : <EmptyState title={L("No dependants", "لا يوجد مرافقون")} />;

  if (tab === "guides") body = (
    <div className="card-b stack" data-testid="guides-list">
      <p className="sm muted" style={{ margin: 0 }}>{L("Step-by-step renewal for every document kind we track: who does it, on which platform, when to start, fees and penalties.", "خطوات تجديد كل نوع وثيقة نتابعه: من ينفذه، وعلى أي منصة، ومتى تبدأ، والرسوم والغرامات.")}</p>
      {GUIDES.map((g) => <div key={g.kind} data-testid={"guide-" + g.kind.toLowerCase().replace(/[^a-z0-9]+/g, "-")}><RenewalGuide kind={g.kind} compact audience="employer" /></div>)}
    </div>
  );

  return (
    <div data-testid="gov">
      <PageHeader
        title={L("Government services", "الخدمات الحكومية")}
        sub={L("Iqama renewals, exit re-entry and final exit visas, transfers, profession changes and their fees — synced with Muqeem and Qiwa.", "تجديد الإقامات وتأشيرات الخروج والعودة والخروج النهائي ونقل الخدمات وتغيير المهن ورسومها.")}
        actions={<>
          <Button icon="link" onClick={() => nav("/gov/integrations")} data-testid="btn-integrations">{L("Integrations", "التكاملات")}</Button>
          <Button variant="pri" icon="plus" onClick={() => setModal(true)} data-testid="btn-new-tx">{L("New transaction", "معاملة جديدة")}</Button>
        </>}
      />
      <Alert tone="info" tint testId="gov-connection-note"
        title={L("Not connected to Muqeem or Qiwa yet", "غير متصل بمقيم أو قوى بعد")}
        desc={L("Transactions are prepared here and completed on the official portals using the guides. Direct submission needs an agreement with Elm (Muqeem) — see Integrations.", "تُجهَّز المعاملات هنا وتُستكمل على البوابات الرسمية باستخدام الأدلة. الإرسال المباشر يتطلب اتفاقية مع عِلم (مقيم) — راجع صفحة التكاملات.")}
        action={<Button variant="sm" onClick={() => nav("/gov/integrations")}>{L("View", "عرض")}</Button>} />
      <section className="card mt">
        <Tabs<Tab> value={tab} onChange={setTab} label={L("Government services sections", "أقسام الخدمات الحكومية")} items={[
          ["transactions", L("Transactions", "المعاملات"), open || null],
          ["fees", L("Fees ledger", "سجل الرسوم")],
          ["dependants", L("Dependants & insurance", "المرافقون والتأمين")],
          ["guides", L("Renewal guides", "أدلة التجديد"), GUIDES.length],
        ]} />
        {body}
      </section>
      {modal && <NewTxModal emps={emps} today={today} onClose={() => setModal(false)} />}
    </div>
  );
}

export default function Gov() {
  const c = useCountry();
  return c.code === "SA" ? <SaudiGov /> : <GovCountry c={c} testId="gov-country" />;
}
